/* stylelint-disable no-descending-specificity */
import { css, type DefaultTheme, styled } from 'styled-components';

import { Styled as StyledButton } from '../Button/styles';
import { Styled as StyledHeading } from '../Heading/styles';
import { Styled as StyledIcon } from '../Icon/styles';
import { Styled as StyledText } from '../Text/styles';

import type { ColoredCardColor } from './types';

type CardColorsCssTokens = {
  getBackgroundColor: ({ theme }: { theme: DefaultTheme }) => string;
  getTextColor: ({ theme }: { theme: DefaultTheme }) => string;
  getButtonTextColor: ({ theme }: { theme: DefaultTheme }) => string;
  getButtonBackgroundColor: ({ theme }: { theme: DefaultTheme }) => string;
  getButtonHoveredBackgroundColor: ({ theme }: { theme: DefaultTheme }) => string;
  getButtonFocusedBackgroundColor: ({ theme }: { theme: DefaultTheme }) => string;
  getIconTextColor: ({ theme }: { theme: DefaultTheme }) => string;
};

type Css = ReturnType<typeof css>;

const getCardColorCss = (tokens: CardColorsCssTokens): Css => css`
  background: ${tokens.getBackgroundColor};

  ${StyledHeading.Div},
  ${StyledText.Span} {
    color: ${tokens.getTextColor};
  }
  ${StyledButton.Button} {
    color: ${tokens.getButtonTextColor};
    background: ${tokens.getButtonBackgroundColor};
    border: 1px solid transparent;
    &:hover,
    &.hover {
      background: ${tokens.getButtonHoveredBackgroundColor};
      border: 1px solid transparent;
    }
    &:focus,
    &.focus {
      background: ${tokens.getButtonFocusedBackgroundColor};
      border: 1px solid transparent;
    }
  }
  ${StyledIcon.Container} {
    color: ${tokens.getIconTextColor};
  }
`;

const stylesByColor: Record<ColoredCardColor, Css> = {
  blue: getCardColorCss({
    getBackgroundColor: ({ theme }) => theme.designTokens.coloredCardBlueBackgroundColor,
    getTextColor: ({ theme }) => theme.designTokens.coloredCardBlueTextColor,
    getButtonTextColor: ({ theme }) => theme.designTokens.coloredCardBlueButtonTextColor,
    getButtonBackgroundColor: ({ theme }) => theme.designTokens.coloredCardBlueButtonBackgroundColor,
    getButtonHoveredBackgroundColor: ({ theme }) => theme.designTokens.coloredCardBlueButtonHoverBackgroundColor,
    getButtonFocusedBackgroundColor: ({ theme }) => theme.designTokens.coloredCardBlueButtonFocusBackgroundColor,
    getIconTextColor: ({ theme }) => theme.designTokens.coloredCardBlueIconTextColor,
  }),
  red: getCardColorCss({
    getBackgroundColor: ({ theme }) => theme.designTokens.coloredCardRedBackgroundColor,
    getTextColor: ({ theme }) => theme.designTokens.coloredCardRedTextColor,
    getButtonTextColor: ({ theme }) => theme.designTokens.coloredCardRedButtonTextColor,
    getButtonBackgroundColor: ({ theme }) => theme.designTokens.coloredCardRedButtonBackgroundColor,
    getButtonHoveredBackgroundColor: ({ theme }) => theme.designTokens.coloredCardRedButtonHoverBackgroundColor,
    getButtonFocusedBackgroundColor: ({ theme }) => theme.designTokens.coloredCardRedButtonFocusBackgroundColor,
    getIconTextColor: ({ theme }) => theme.designTokens.coloredCardRedIconTextColor,
  }),
  green: getCardColorCss({
    getBackgroundColor: ({ theme }) => theme.designTokens.coloredCardGreenBackgroundColor,
    getTextColor: ({ theme }) => theme.designTokens.coloredCardGreenTextColor,
    getButtonTextColor: ({ theme }) => theme.designTokens.coloredCardGreenButtonTextColor,
    getButtonBackgroundColor: ({ theme }) => theme.designTokens.coloredCardGreenButtonBackgroundColor,
    getButtonHoveredBackgroundColor: ({ theme }) => theme.designTokens.coloredCardGreenButtonHoverBackgroundColor,
    getButtonFocusedBackgroundColor: ({ theme }) => theme.designTokens.coloredCardGreenButtonFocusBackgroundColor,
    getIconTextColor: ({ theme }) => theme.designTokens.coloredCardGreenIconTextColor,
  }),
  orange: getCardColorCss({
    getBackgroundColor: ({ theme }) => theme.designTokens.coloredCardOrangeBackgroundColor,
    getTextColor: ({ theme }) => theme.designTokens.coloredCardOrangeTextColor,
    getButtonTextColor: ({ theme }) => theme.designTokens.coloredCardOrangeButtonTextColor,
    getButtonBackgroundColor: ({ theme }) => theme.designTokens.coloredCardOrangeButtonBackgroundColor,
    getButtonHoveredBackgroundColor: ({ theme }) => theme.designTokens.coloredCardOrangeButtonHoverBackgroundColor,
    getButtonFocusedBackgroundColor: ({ theme }) => theme.designTokens.coloredCardOrangeButtonFocusBackgroundColor,
    getIconTextColor: ({ theme }) => theme.designTokens.coloredCardOrangeIconTextColor,
  }),
  purple: getCardColorCss({
    getBackgroundColor: ({ theme }) => theme.designTokens.coloredCardPurpleBackgroundColor,
    getTextColor: ({ theme }) => theme.designTokens.coloredCardPurpleTextColor,
    getButtonTextColor: ({ theme }) => theme.designTokens.coloredCardPurpleButtonTextColor,
    getButtonBackgroundColor: ({ theme }) => theme.designTokens.coloredCardPurpleButtonBackgroundColor,
    getButtonHoveredBackgroundColor: ({ theme }) => theme.designTokens.coloredCardPurpleButtonHoverBackgroundColor,
    getButtonFocusedBackgroundColor: ({ theme }) => theme.designTokens.coloredCardPurpleButtonFocusBackgroundColor,
    getIconTextColor: ({ theme }) => theme.designTokens.coloredCardPurpleIconTextColor,
  }),
  yellow: getCardColorCss({
    getBackgroundColor: ({ theme }) => theme.designTokens.coloredCardYellowBackgroundColor,
    getTextColor: ({ theme }) => theme.designTokens.coloredCardYellowTextColor,
    getButtonTextColor: ({ theme }) => theme.designTokens.coloredCardYellowButtonTextColor,
    getButtonBackgroundColor: ({ theme }) => theme.designTokens.coloredCardYellowButtonBackgroundColor,
    getButtonHoveredBackgroundColor: ({ theme }) => theme.designTokens.coloredCardYellowButtonHoverBackgroundColor,
    getButtonFocusedBackgroundColor: ({ theme }) => theme.designTokens.coloredCardYellowButtonFocusBackgroundColor,
    getIconTextColor: ({ theme }) => theme.designTokens.coloredCardYellowIconTextColor,
  }),
};

const Card = styled.div<{ color: ColoredCardColor }>`
  padding: ${({ theme }) => theme.designTokens.coloredCardPaddingY}
    ${({ theme }) => theme.designTokens.coloredCardPaddingX};
  border-radius: ${({ theme }) => theme.designTokens.coloredCardRadius};
  border: 1px solid transparent;

  ${({ color }) => stylesByColor[color]}
`;

export const Styled = {
  Card,
};
